import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress, FormControl, Select, MenuItem, Tooltip, IconButton } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { apiClient } from '@/services/api';
import ChatArea from '@/components/playground/ChatArea';
import MessageInput from '@/components/playground/MessageInput';

interface Conversation {
    id: string;
    title: string;
    messages: string;
    created_at: string;
}

interface WebSocketMessage {
    id: string;
    content: string;
    sender: 'user' | 'bot';
    type: 'message' | 'response' | 'error' | 'system';
    timestamp: string;
    conversation_id?: string;
    metadata?: {
        timeoutId?: NodeJS.Timeout | null;
        [key: string]: any;
    };
}

const ChatPanel: React.FC = () => {
    const { projectId: projectIdStr } = useParams<{ projectId: string }>();
    const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;
    const { user } = useAuth();

    // WebSocket State
    const {
        isConnected,
        sendMessage,
        onMessage,
        subscribeProject
    } = useWebSocket();

    // Local State
    const [loading, setLoading] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [messages, setMessages] = useState<WebSocketMessage[]>([]);
    const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
    const [initializing, setInitializing] = useState(true);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [autoSaveNextResponse, setAutoSaveNextResponse] = useState(false);
    const [autoSaveQuery, setAutoSaveQuery] = useState('');
    const [projectType, setProjectType] = useState<string | undefined>(undefined);

    // Initialize Chat
    useEffect(() => {
        const initChat = async () => {
            if (!user || !projectId) return;

            try {
                // 获取项目信息（包括项目类型）
                try {
                    const projectResponse = await apiClient.get(`/projects/${projectId}`);
                    if (projectResponse?.success && projectResponse.data) {
                        setProjectType(projectResponse.data.project_type);
                    }
                } catch (error) {
                }

                // Load conversation list
                const response = await apiClient.conversations.getList(projectId.toString(), user.id.toString());
                if (Array.isArray(response)) {
                    setConversations(response);
                    if (response.length > 0) {
                        const latest = response[0];
                        setCurrentConversation(latest);
                        await loadConversationMessages(latest);
                    } else {
                        await createNewConversation();
                    }
                }
            } catch (error) {
            } finally {
                setInitializing(false);
            }
        };

        initChat();
    }, [user, projectId]);

    // Listen for AI Log Generation Trigger
    useEffect(() => {
        const handleAiFactGeneration = async (e: Event) => {
            const event = e as CustomEvent;
            const prompt = event.detail?.prompt;
            if (prompt) {
                // Switch to a new conversation or ensure we are ready
                if (!currentConversation) {
                    await createNewConversation();
                }

                // Set flags for auto-saving
                setAutoSaveNextResponse(true);
                setAutoSaveQuery(prompt);

                // Send the message
                // We need to simulate user input and send
                // Since handleSendMessage uses userInput state, we can't easily use it directly without setting state and waiting.
                // Instead, we'll call the internal logic directly.

                setLoading(true);

                const userMessage: WebSocketMessage = {
                    id: `user-${Date.now()}`,
                    content: prompt,
                    sender: 'user',
                    type: 'message',
                    timestamp: new Date().toISOString(),
                    conversation_id: currentConversation?.id
                };

                setMessages(prev => [...prev, userMessage]);

                sendMessage(prompt, {
                    project_id: projectId?.toString(),
                    conversation_id: currentConversation?.id
                });
            }
        };

        window.addEventListener('triggerAiFactGeneration', handleAiFactGeneration as EventListener);
        return () => {
            window.removeEventListener('triggerAiFactGeneration', handleAiFactGeneration as EventListener);
        };
    }, [currentConversation, projectId, sendMessage]);

    // WebSocket Connection
    useEffect(() => {
        if (isConnected && projectId) {
            subscribeProject(projectId);
        }
    }, [isConnected, projectId, subscribeProject]);

    // Handle WebSocket Messages
    useEffect(() => {
        const unsubscribe = onMessage((message) => {
            if (message.type === 'message.completed' || message.type === 'response' || message.type === 'error') {
                setLoading(false);

                if (message.content && message.content.trim()) {
                    const msgAny = message as any;
                    const newMessage: WebSocketMessage = {
                        id: msgAny.message_id || `ws-${Date.now()}`,
                        content: message.content.trim(),
                        sender: message.type === 'error' ? 'user' : 'bot',
                        type: message.type === 'error' ? 'error' : 'message',
                        timestamp: new Date().toISOString(),
                        conversation_id: currentConversation?.id
                    };

                    setMessages(prev => {
                        // Avoid duplicates
                        const exists = prev.some(msg =>
                            msg.content === newMessage.content &&
                            msg.sender === newMessage.sender &&
                            Math.abs(new Date(msg.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 5000
                        );
                        return exists ? prev : [...prev, newMessage];
                    });

                    // Handle Auto-Save
                    if (autoSaveNextResponse && message.type !== 'error') {
                        handleSaveAsFact(autoSaveQuery, message.content.trim());
                        setAutoSaveNextResponse(false);
                        setAutoSaveQuery('');
                        alert('事实已保存');
                    }
                }
            }
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [onMessage, currentConversation, autoSaveNextResponse, autoSaveQuery]);

    const loadConversationMessages = async (conversation: Conversation) => {
        if (!projectId) return;
        try {
            const response = await apiClient.conversations.getById(projectId.toString(), conversation.id);
            if (response) {
                let messagesData: any[] = [];
                if (response.messages && Array.isArray(response.messages)) {
                    messagesData = response.messages;
                } else if (response.message_list && Array.isArray(response.message_list)) {
                    messagesData = response.message_list;
                } else if (response.messages && typeof response.messages === 'string') {
                    messagesData = JSON.parse(response.messages || '[]');
                }

                const historyMessages: WebSocketMessage[] = messagesData.map((msg: any, index: number) => ({
                    id: msg.id ? msg.id.toString() : `${conversation.id}-${index}`,
                    content: msg.content,
                    sender: msg.role === 'user' ? 'user' : 'bot',
                    type: msg.role === 'error' ? 'error' : 'message',
                    timestamp: msg.timestamp || msg.created_at || new Date().toISOString(),
                    conversation_id: conversation.id
                }));
                setMessages(historyMessages);
            }
        } catch (error) {
        }
    };

    const createNewConversation = async () => {
        if (!user || !projectId) return;
        try {
            // 格式化日期时间：2025/11/23 17:52 星期二
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
            const weekday = weekdays[now.getDay()];
            const title = `${year}/${month}/${day} ${hours}:${minutes} ${weekday}`;
            const response = await apiClient.conversations.create(projectId.toString(), user.id.toString(), title);
            if (response && response.id) {
                const newConv = response;
                setConversations(prev => [newConv, ...prev]);
                setCurrentConversation(newConv);
                setMessages([]);
            }
        } catch (error) {
        }
    };

    const handleConversationChange = async (event: any) => {
        const convId = event.target.value;
        const conv = conversations.find(c => c.id === convId);
        if (conv) {
            setCurrentConversation(conv);
            await loadConversationMessages(conv);
        }
    };

    const handleSaveAsFact = async (query: string, response: string) => {
        if (!projectId) return;
        try {
            // 获取当前项目的所有日志，找出"探索结果"的最大编号
            let maxNumber = 0;
            try {
                const logsResponse = await apiClient.projectFacts.getList(projectId);
                if (logsResponse.success && logsResponse.data) {
                    const logs = Array.isArray(logsResponse.data) ? logsResponse.data :
                        (logsResponse.data?.logs ? logsResponse.data.logs : []);
                    
                    // 找出所有以"探索结果"开头的标题，提取编号
                    logs.forEach((log: any) => {
                        const title = log.title || '';
                        const match = title.match(/^探索结果(\d+)$/);
                        if (match) {
                            const num = parseInt(match[1], 10);
                            if (num > maxNumber) {
                                maxNumber = num;
                            }
                        }
                    });
                }
            } catch (err) {
            }
            
            // 生成下一个编号的标题
            const nextNumber = maxNumber + 1;
            const title = `探索结果${nextNumber}`;
            
            await apiClient.projectFacts.create(projectId, {
                title: title,
                content: `**问题：**\n${query}\n\n**回答：**\n${response}`,
                log_type: 'general',
                source: 'AI探索',
                tags_json: JSON.stringify(['AI对话']),
                source_conversation_id: currentConversation?.id ? parseInt(currentConversation.id) : undefined
            });
            // Trigger log refresh
            window.dispatchEvent(new CustomEvent('projectFactsShouldRefresh'));
        } catch (error) {
        }
    };

    const handleSendMessage = async () => {
        if (!userInput.trim() || loading) return;

        let conversation = currentConversation;
        if (!conversation && user && projectId) {
            await createNewConversation();
        }

        setLoading(true);

        const userMessage: WebSocketMessage = {
            id: `user-${Date.now()}`,
            content: userInput,
            sender: 'user',
            type: 'message',
            timestamp: new Date().toISOString(),
            conversation_id: currentConversation?.id
        };

        setMessages(prev => [...prev, userMessage]);

        sendMessage(userInput, {
            project_id: projectId?.toString(),
            conversation_id: currentConversation?.id
        });

        setUserInput('');
    };

    // Convert messages for ChatArea
    const conversationsData = useMemo(() => {
        const modelId = 'default';
        return {
            [modelId]: messages.map(msg => {
                const parsedId = parseInt(msg.id);
                return {
                    id: isNaN(parsedId) ? undefined : parsedId,
                    role: (msg.sender === 'user' ? 'user' : 'assistant') as 'user' | 'assistant' | 'error',
                    content: msg.content,
                    timestamp: msg.timestamp,
                    is_error: msg.type === 'error'
                };
            })
        };
    }, [messages]);

    if (initializing) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ minWidth: 'fit-content', fontWeight: 600 }}>探索调研</Typography>

                <FormControl size="small" fullWidth>
                    <Select
                        value={currentConversation?.id || ''}
                        onChange={handleConversationChange}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) return <em>选择历史会话</em>;
                            const conv = conversations.find(c => c.id === selected);
                            return conv ? conv.title : selected;
                        }}
                    >
                        <MenuItem value="" disabled>
                            <em>选择历史会话</em>
                        </MenuItem>
                        {conversations.map((conv) => (
                            <MenuItem key={conv.id} value={conv.id}>
                                <Tooltip title="保存为事实"><span>{conv.title}</span></Tooltip>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <IconButton 
                    size="small" 
                    onClick={createNewConversation} 
                    sx={{ 
                        minWidth: 'fit-content',
                        border: 1,
                        borderColor: 'divider',
                        '&:hover': {
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
                <ChatArea
                    selectedModels={['default']}
                    conversations={conversationsData}
                    loading={{ 'default': loading }}
                    getModelName={() => 'DeepWriter AI'}
                    onClearConversation={() => setMessages([])}
                    onSaveFact={handleSaveAsFact} // Pass this prop to ChatArea
                />
            </Box>

            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <MessageInput
                    userInput={userInput}
                    handleInputChange={(e) => setUserInput(e.target.value)}
                    handleSendMessage={handleSendMessage}
                    handleKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                    loading={{ 'default': loading }}
                    selectedModels={['default']}
                    isConnected={isConnected}
                    projectId={projectId || undefined}
                    projectType={projectType}
                />
            </Box>
        </Box>
    );
};

export default ChatPanel;
